// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React from 'react';
import {
    Badge,
    Box,
    Button,
    Container,
    ContentLayout,
    ExpandableSection,
    Grid,
    Header,
    Link,
    SpaceBetween,
} from '@cloudscape-design/components';
import * as appProblemAlerts from '../Errors/DeploymentHealthcheck/Alerts';
import * as R from 'ramda';
import {useGetApplicationProblems} from '../Hooks/useGetApplicationProblems';
import {isUsingOrganizations, isUsingSSO} from '../../Utils/AccountUtils';
import {CREATE_DIAGRAM, IMPORT as IMPORT_ROUTE, ONBOARDING_GUIDE, RESOURCES} from '../../routes';

// Version is changed in ResourceLensCDK/lib/config/constants.ts in the SOLUTION_METADATA constant.
const getChangelog = () => [
    {
        version: '1.1',
        date: 'February 2026',
        changes: [
            {
                title: 'CLI Onboarding',
                description: 'New CLI-based onboarding via Builder Toolbox for quick one-time account discovery (~5 minutes). Perfect for trying out ResourceLens or accounts without admin access.',
                link: ONBOARDING_GUIDE,
                linkText: 'View Onboarding Guide',
            },
            {
                title: 'Auto-Generated Resource Filtering',
                description: 'Filter resources using custom keywords, prefixes, or auto-generated filters. Apply filters during resource discovery, while building diagrams, or to existing diagrams with real-time updates.',
                link: CREATE_DIAGRAM,
                linkText: 'Try it out',
            },
        ],
    },
];

function renderAlerts(problems) {
    const alerts = problems.map(problem => {
        const AlertComponent =
            appProblemAlerts[`${problem.name}Alert`] ??
            appProblemAlerts.GenericErrorAlert;

        return <AlertComponent key={problem.name} {...problem} />;
    });

    return <SpaceBetween size="s">{alerts}</SpaceBetween>;
}

const Homepage = () => {
    const {data: appProblems = {}} = useGetApplicationProblems();
    const hasProblems = !R.isEmpty(appProblems.logProblems ?? []);

    const items = [
        {
            header: 'Import Accounts',
            body: 'Import regions for one or more AWS accounts',
            link: IMPORT_ROUTE,
            isHiddenInOrgMode: true,
        },
        {
            header: 'Add Users',
            body: 'Create users in Cognito to grant access to the application',
            isHiddenWhenUsingSSO: true,
        },
        {
            header: 'Create Diagram',
            body: 'Create a diagram to visualise resources',
            link: CREATE_DIAGRAM,
        },
        {
            header: 'Search Resources',
            body: 'Search for discovered resources in imported accounts',
            link: RESOURCES,
        },
        {
            header: 'Onboarding Guide',
            body: 'Learn how to onboard AWS accounts to ResourceLens',
            link: ONBOARDING_GUIDE,
            isNew: true,
        },
    ];

    const homepageItems = items.filter(
        ({isHiddenInOrgMode, isHiddenWhenUsingSSO}) => {
            if (isHiddenInOrgMode && isUsingOrganizations()) return false;

            if (isHiddenWhenUsingSSO && isUsingSSO()) return false;

            return true;
        }
    );

    // Create grid definition
    const gridDefinition = [
        // Add a single full-width row for alerts if they exist
        ...(hasProblems ? [{colspan: 12}] : []),
        // Add a 3-column row for each item
        ...homepageItems.map(() => ({colspan: {default: 6}})),
    ];

    return (
        <ContentLayout
            defaultPadding
            disableOverlap
            headerVariant="high-contrast"
            maxContentWidth={1000}
            header={
                <>
                    <Box
                        variant="h1"
                        fontWeight="heavy"
                        padding={{top: 'xs'}}
                        fontSize="display-l"
                    >
                        ResourceLens
                    </Box>
                    <Box
                        fontWeight="light"
                        padding={{bottom: 's'}}
                        fontSize="display-l"
                    >
                        Explore your AWS Workloads
                    </Box>
                    <Box variant="p" fontWeight="light">
                        Build, customize, and share detailed architecture
                        diagrams of your workloads based on live data from AWS.
                    </Box>
                </>
            }
        >
            <Box padding={{top: 'xxxl', horizontal: ''}}>
                <SpaceBetween size="xl">
                    <Grid gridDefinition={gridDefinition}>
                        {hasProblems && (
                            <Box padding={{bottom: 'l'}}>
                                {renderAlerts(appProblems.logProblems)}
                            </Box>
                        )}
                        {homepageItems.map(({header, body, link, isNew}) => (
                            <Item
                                key={header}
                                header={header}
                                body={body}
                                link={link}
                                isNew={isNew}
                            />
                        ))}
                    </Grid>
                    <ChangelogSection />
                </SpaceBetween>
            </Box>
        </ContentLayout>
    );
};

const Item = ({header, body, link, isNew}) => (
    <Container
        header={
            <Header
                actions={
                    link ? (
                        <Button href={link} variant="primary">
                            {header}
                        </Button>
                    ) : null
                }
                description={body}
            >
                <SpaceBetween direction="horizontal" size="xs">
                    {header}
                    {isNew && <Badge color="green">New</Badge>}
                </SpaceBetween>
            </Header>
        }
    ></Container>
);

const ChangelogSection = () => {
    const changelog = getChangelog();
    return (
        <Container
            header={
                <Header variant="h2">
                    What&apos;s New
                </Header>
            }
        >
            <SpaceBetween size="l">
                {changelog.map(({version, date, changes}) => (
                    <ExpandableSection
                        key={version}
                        defaultExpanded={version === changelog[0].version}
                    headerText={
                        <SpaceBetween direction="horizontal" size="xs">
                            <Box fontWeight="bold">Version {version}</Box>
                            <Box color="text-status-inactive">— {date}</Box>
                        </SpaceBetween>
                    }
                >
                    <SpaceBetween size="m">
                        {changes.map(({title, description, link, linkText}) => (
                            <Box key={title}>
                                <Box fontWeight="bold">{title}</Box>
                                <Box variant="p" color="text-body-secondary">
                                    {description}
                                    {link && (
                                        <>
                                            {' '}
                                            <Link href={link}>{linkText}</Link>
                                        </>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </SpaceBetween>
                </ExpandableSection>
                ))}
            </SpaceBetween>
        </Container>
    );
};

export default Homepage;
